/**
 * API client for the new ConfigObject endpoints
 * (/api/v1/designer/config/*)
 */
import { ApiService } from '@/utils/api'

// ---------------------------------------------------------------------------
// Types mirroring backend config_model.py
// ---------------------------------------------------------------------------

export type ObjectType =
  | 'catalog'
  | 'document'
  | 'register_accumulation'
  | 'register_info'
  | 'enum'
  | 'constant'
  | 'sequence'

export type AttributeType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'time'
  | 'uuid'
  | 'text'
  | 'ref'
  | 'enum_ref'
  | 'composite_ref'
  | 'tabular'
  | 'computed'
  | 'binary'

export interface ObjectTraits {
  hierarchical?: boolean
  has_owner?: boolean
  owner_type?: string
  owner_name?: string
  has_code?: boolean
  code_length?: number
  code_type?: string
  has_number?: boolean
  number_length?: number
  number_prefix?: string
  autonumbering_field?: string
  presentation_field?: string
  presentation_template?: string
  periodical?: boolean
  postable?: boolean
  draft_mode?: boolean
  soft_delete?: boolean
  versionable?: boolean
  computed_fields?: boolean
  row_level_security?: boolean
}

export interface AttributeDef {
  name: string
  synonym: string
  synonyms?: Record<string, string>
  /** STRING attribute only: generates a per-language sibling `<name>__<locale>` field. */
  translatable?: boolean
  attr_type: AttributeType
  target?: string
  required: boolean
  indexed: boolean
  unique: boolean
  hint: string
  props: Record<string, any>
  is_standard: boolean
  order: number
  module_key?: string | null
  columns?: AttributeDef[] | null
}

export interface PredefinedElement {
  predefined_name: string
  values: Record<string, unknown>
}

/** Result of materializing predefined rows into the connection DB on save. */
export interface PredefinedApplyResult {
  created: number
  skipped: number
  errors: string[]
}

/** Result of deleting predefined rows dropped from the `predefined` patch on save. */
export interface PredefinedRemoveResult {
  deleted: number
  warnings: string[]
}

export interface ConfigObject {
  id: string
  connection_id: string
  obj_type: ObjectType
  name: string
  synonym: string
  synonyms?: Record<string, string>
  comment: string
  traits: ObjectTraits
  attributes: AttributeDef[]
  register_type: string
  dimensions: AttributeDef[]
  resources: AttributeDef[]
  details: AttributeDef[]
  enum_values: Array<{ name: string; synonym: string; order: number; synonyms?: Record<string, string> }>
  value_type?: AttributeType | null
  value_props: Record<string, any>
  sequence_start: number
  sequence_step: number
  sequence_padding: number
  forms: Record<string, any>
  layouts: Record<string, any>
  module_code: string
  predefined?: PredefinedElement[]
  created_at: string
  updated_at: string
  module_key?: string | null
}

export interface ConfigObjectMeta {
  id: string
  name: string
  synonym: string
  obj_type: ObjectType
  module_key?: string | null
  has_forms: boolean
  has_module: boolean
}

export interface ConfigTreeResponse {
  catalogs: ConfigObjectMeta[]
  documents: ConfigObjectMeta[]
  registers_accumulation: ConfigObjectMeta[]
  registers_info: ConfigObjectMeta[]
  enums: ConfigObjectMeta[]
  constants: ConfigObjectMeta[]
  sequences: ConfigObjectMeta[]
}

export interface ConfigObjectCreate {
  obj_type: ObjectType
  name: string
  synonym?: string
  comment?: string
  traits?: ObjectTraits
}

export interface ConfigObjectUpdate {
  name?: string
  synonym?: string
  comment?: string
  traits?: ObjectTraits
  module_code?: string
  forms?: Record<string, any>
  layouts?: Record<string, any>
  predefined?: PredefinedElement[]
}

export interface AttributeDefCreate {
  name: string
  synonym?: string
  attr_type?: AttributeType
  required?: boolean
  indexed?: boolean
  unique?: boolean
  hint?: string
  props?: Record<string, any>
  columns?: AttributeDefCreate[]
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const BASE = '/v1/designer/config'

function qs(connectionId: string): string {
  return `connection_id=${encodeURIComponent(connectionId)}`
}

export const configObjectApi = {
  /** Get the configuration tree */
  getTree: (connectionId: string) =>
    ApiService.get<ConfigTreeResponse>(`${BASE}/tree?${qs(connectionId)}`),

  /** Create a new config object */
  createObject: (connectionId: string, data: ConfigObjectCreate) =>
    ApiService.post<ConfigObject>(`${BASE}/objects?${qs(connectionId)}`, data),

  /** Get a full config object by name */
  getObject: (connectionId: string, name: string) =>
    ApiService.get<ConfigObject>(`${BASE}/objects/${encodeURIComponent(name)}?${qs(connectionId)}`),

  /** Update a config object. When `predefined` is in the patch, the response
   *  may include `predefined_apply` (materialize result) and, when predefined
   *  names were removed, `predefined_remove` (delete result). */
  updateObject: (connectionId: string, name: string, data: ConfigObjectUpdate) =>
    ApiService.put<
      ConfigObject & {
        predefined_apply?: PredefinedApplyResult
        predefined_remove?: PredefinedRemoveResult
      }
    >(`${BASE}/objects/${encodeURIComponent(name)}?${qs(connectionId)}`, data),

  /** Delete a config object */
  deleteObject: (connectionId: string, name: string) =>
    ApiService.delete(`${BASE}/objects/${encodeURIComponent(name)}?${qs(connectionId)}`),

  /** Add an attribute */
  addAttribute: (connectionId: string, objectName: string, attr: AttributeDefCreate) =>
    ApiService.post<ConfigObject>(
      `${BASE}/objects/${encodeURIComponent(objectName)}/attributes?${qs(connectionId)}`,
      attr,
    ),

  /** Update an attribute */
  updateAttribute: (connectionId: string, objectName: string, attrName: string, attr: AttributeDefCreate) =>
    ApiService.put<ConfigObject>(
      `${BASE}/objects/${encodeURIComponent(objectName)}/attributes/${encodeURIComponent(attrName)}?${qs(connectionId)}`,
      attr,
    ),

  /** Delete an attribute */
  deleteAttribute: (connectionId: string, objectName: string, attrName: string) =>
    ApiService.delete(
      `${BASE}/objects/${encodeURIComponent(objectName)}/attributes/${encodeURIComponent(attrName)}?${qs(connectionId)}`,
    ),

  /** Regenerate ItemForm/ListForm from current attributes */
  regenerateForms: (connectionId: string, objectName: string, kinds?: ('item' | 'list')[]) => {
    const kindsQs = kinds?.length ? `&kinds=${kinds.join(',')}` : ''
    return ApiService.post<{ forms: Record<string, unknown> }>(
      `${BASE}/objects/${encodeURIComponent(objectName)}/regenerate-forms?${qs(connectionId)}${kindsQs}`,
      {},
    )
  },

  scanHealth: (
    connectionId: string,
    designerTree?: unknown[],
  ) =>
    ApiService.post<{
      issues: Array<{
        severity: string
        code: string
        object_name: string
        message: string
        fix?: string | null
      }>
      scanned_objects: number
      fixable_count: number
      drift_checked: boolean
    }>(`${BASE}/health/scan`, {
      connection_id: connectionId,
      designer_tree: designerTree ?? null,
    }),

  fixHealth: (connectionId: string, designerTree?: unknown[]) =>
    ApiService.post<{
      applied: string[]
      issues_remaining: Array<{
        severity: string
        code: string
        object_name: string
        message: string
        fix?: string | null
      }>
      merged_tree?: unknown[] | null
    }>(`${BASE}/health/fix`, {
      connection_id: connectionId,
      designer_tree: designerTree ?? null,
    }),
}
